import { Add, Button, Descriptions, Edit, Heading } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { DescriptionsItemType } from 'antd/es/descriptions'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { Link } from 'react-router-dom'
import { useRoleAccess } from 'components/AccessControl'
import { UserRole } from '@lmc/ts-common'

type ProfileViewSectionProps = {
  items?: DescriptionsItemType[]
  title: string
  icon?: ReactNode
  action?: () => void
  link?: string
  isEditMode?: boolean
  subtitle?: React.ReactNode
  isDesktop?: boolean
  children?: ReactNode
}

export const ProfileViewSection = ({
  items,
  title,
  icon,
  action,
  link,
  subtitle,
  isEditMode = Boolean(items?.length),
  isDesktop = true,
  children,
}: ProfileViewSectionProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  if (Boolean(action) && Boolean(link)) {
    throw new Error(
      "You can't provide both {action} and {link} props to {ProfileViewSection}"
    )
  }

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin, UserRole.seniorCarer],
    [
      {
        i: 'create',
        this: 'Resident',
      },
      {
        i: 'update',
        this: 'Resident',
      },
    ]
  )

  const buttonContent = (
    <ButtonWrapper>
      {isEditMode ? <Edit /> : <Add />}
      {t(supConstants[isEditMode ? 'EDIT' : 'ADD'])}
    </ButtonWrapper>
  )

  return (
    <MainWrapper
      data-cy={`container-${title.split(' ').slice(0, -1).join('')}`}
    >
      <TopWrapper>
        <InfoWrapper>
          {icon && icon}
          <TitlesWrapper>
            <Heading level={3}>{title}</Heading>
            {subtitle}
          </TitlesWrapper>
        </InfoWrapper>
        {hasRolePermission && (
          <StyledButton
            data-cy={`editBtn-${title.split(' ').slice(0, -1).join('')}`}
            variant="text"
            onClick={action}
          >
            {link ? <Link to={link}>{buttonContent}</Link> : buttonContent}
          </StyledButton>
        )}
      </TopWrapper>
      {Boolean(items?.length) && (
        <DescWrapper>
          <Descriptions
            labelStyle={labelStyle}
            items={items}
            bordered
            column={1}
            layout={!isDesktop ? 'vertical' : 'horizontal'}
          />
        </DescWrapper>
      )}
      {children}
    </MainWrapper>
  )
}

const labelStyle = { width: 152 }

const TitlesWrapper = styled.div`
  margin-left: 8px;
  display: flex;
  flex-direction: column;
`

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ButtonWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
  flex: 1;
`

const TopWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const StyledButton = styled(Button)`
  margin-left: 5px;
`

const DescWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color8};
  padding-top: 16px;
  margin-top: 16px;
`
